import React from 'react';
import { useCMS } from '../../context/CMSContext';
import { ArrowLinkText } from '../common/ArrowLinkText';
import { getComputedTextStyle } from '../../utils/typographyHelper';
import { ScrollReveal } from '../common/ScrollReveal';
import { handleCtaNavigation } from '../../utils/navigationHelper';
import { OptimizedImage } from '../common/OptimizedImage';

export const StorySection: React.FC = () => {
  const { data, setIsStoryModalOpen, setIsProspectusModalOpen, setActivePublicPage } = useCMS();
  const { story, typography } = data;

  const handleCtaClick = () => {
    // Ensure popup is closed
    setIsStoryModalOpen(false);

    // Determine target from admin configuration
    const isExternal = story.ctaLinkType === 'external' && Boolean(story.ctaExternalUrl);
    const target = isExternal ? (story.ctaExternalUrl || 'about') : (story.ctaTargetPage || 'about');

    handleCtaNavigation(
      target,
      story.ctaExternalUrl,
      {
        setActivePublicPage,
        openProspectusModal: () => setIsProspectusModalOpen(true),
        defaultTarget: 'about'
      }
    );
  };

  const tagStyle = getComputedTextStyle(
    typography?.storyTag,
    typography?.sectionTags,
    { fontSize: 11, fontWeight: '500', fontColor: '#827f79', letterSpacing: '0.25em', textTransform: 'uppercase' }
  );

  const headingStyle = getComputedTextStyle(
    typography?.storyHeading,
    typography?.h2 || typography?.sectionHeadings,
    {
      fontSize: 56,
      fontWeight: typography?.allHeadingFontWeight || '300',
      fontColor: '#0a122a',
      lineHeight: 1.12,
      letterSpacing: '-0.02em'
    }
  );

  const paragraphStyle = getComputedTextStyle(
    typography?.storyParagraphs,
    typography?.bodyParagraphs || typography?.sectionParagraphs,
    { fontSize: 15, fontWeight: '300', fontColor: '#0a122a', lineHeight: 1.65 }
  );

  const hasLine1 = Boolean(story.headingLine1 && story.headingLine1.trim());
  const hasLine2 = Boolean(story.headingLine2 && story.headingLine2.trim());

  return (
    <section id="story" className="py-24 md:py-32 bg-[#f7f2e8] text-[#1e2024] transition-colors">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          
          {/* Left Column Content */}
          <div className="lg:col-span-6 space-y-8">
            {/* Tag matching screenshot */}
            <ScrollReveal delay={0}>
              <div className="flex items-center space-x-3" style={tagStyle}>
                <span>{story.tag || 'EXPLORE OUR PROJECTS'}</span>
                <span className="w-12 h-[1px] bg-current opacity-60 inline-block"></span>
              </div>
            </ScrollReveal>

            {/* Heading matching screenshot */}
            <ScrollReveal delay={120}>
              <h2
                className="font-jost leading-[1.12] tracking-tight"
                style={headingStyle}
              >
                {hasLine1 && <div>{story.headingLine1}</div>}
                {hasLine2 && <div>{story.headingLine2}</div>}
                {!hasLine1 && !hasLine2 && <div>Beyond Buildings.</div>}
              </h2>
            </ScrollReveal>

            {/* Paragraphs matching screenshot */}
            <ScrollReveal delay={240}>
              <div className="space-y-6 max-w-xl">
                {story.paragraphs.map((para, idx) => (
                  <p key={idx} className="font-jost" style={paragraphStyle}>
                    {para}
                  </p>
                ))}
              </div>
            </ScrollReveal>

            {/* Know Our Story Link matching screenshot */}
            {Boolean(story.ctaText && story.ctaText.trim()) && (
              <ScrollReveal delay={360}>
                <div className="pt-2">
                  <button
                    onClick={handleCtaClick}
                    className="inline-flex items-center text-xs tracking-[0.25em] text-[#846f55] hover:text-[#a88345] uppercase font-semibold group cursor-pointer font-sans transition-colors"
                  >
                    <ArrowLinkText text={story.ctaText} defaultText={story.ctaText} />
                  </button>
                </div>
              </ScrollReveal>
            )}
          </div>

          {/* Right Column Image matching screenshot */}
          <div className="lg:col-span-6">
            <ScrollReveal delay={180} variant="image">
              <div className="relative overflow-hidden group shadow-lg">
                <div className="aspect-[4/3] md:aspect-[5/4] w-full overflow-hidden bg-[#181a1f]">
                  {story.storyImageUrl && story.storyImageUrl.trim() !== '' ? (
                    <OptimizedImage
                      src={story.storyImageUrl}
                      alt="Oltraya Architectural Craft"
                      width={640}
                      height={480}
                      sizes="(max-width: 768px) 100vw, 640px"
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover object-center group-hover:scale-103 transition-transform duration-700 ease-out"
                      pictureClassName="w-full h-full block"
                    />
                  ) : (
                    <div className="w-full h-full bg-[#181a1f] flex items-center justify-center text-[#8c8a85] text-xs uppercase tracking-widest">
                      No image uploaded
                    </div>
                  )}
                </div>
              </div>
            </ScrollReveal>
          </div>

        </div>
      </div>
    </section>
  );
};
